import { useCallback, useEffect, useState } from 'react';
import { AppRoutes } from './components/AppRoutes';
import { COMMAND_PALETTE_EVENT } from './components/AppShell';
import { CommandPalette } from './components/ui/CommandPalette';
import { ToastProvider } from './components/ui';
import { AuthProvider } from './lib/auth';
import { ScopeProvider } from './lib/scope';
import { ThemeProvider } from './lib/theme';

/**
 * Providers, routes, and the one thing that lives above the routes: the command
 * palette. Order matters — theme first so nothing renders before the surfaces are
 * settled, auth next because every route asks it a question, toasts last so a
 * provider below them can raise one.
 */

function CommandPaletteMount() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Cmd-K on a Mac, Ctrl-K elsewhere. Ignored while a dialog above has the
      // keyboard, and while the user is mid-word in a field that already handles it.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    const onRequest = () => setOpen(true);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(COMMAND_PALETTE_EVENT, onRequest);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(COMMAND_PALETTE_EVENT, onRequest);
    };
  }, []);

  return <CommandPalette open={open} onClose={close} />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ScopeProvider>
          <ToastProvider>
            <AppRoutes />
            <CommandPaletteMount />
          </ToastProvider>
        </ScopeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
